import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRobot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getApiUrl, SERVERS, ENDPOINTS } from '../config/api';
import './AutomatedEvaluation.css';

const AutomatedEvaluation = () => {
  const navigate = useNavigate();
  const normalizeText = (value) => (value || '').toString().trim().replace(/\s+/g, ' ');

  const getCourseKey = (courseCode, courseName) => `${normalizeText(courseCode).toLowerCase()}||${normalizeText(courseName).toLowerCase()}`;

  const getExamTypeLabel = (examType) => {
    if (typeof examType === 'string') {
      return normalizeText(examType);
    }

    if (examType && typeof examType === 'object') {
      return normalizeText(examType.exam_type || examType.examType || examType.name || examType.label || '');
    }

    return '';
  };

  const getEvaluationTypeLabel = (examType, fallbackValue = '') => {
    if (examType && typeof examType === 'object') {
      return normalizeText(examType.evaluation_type || examType.evaluationType || fallbackValue || '');
    }

    return normalizeText(fallbackValue || '');
  };

  const parseSemesterName = (semesterName) => {
    const normalizedSemester = normalizeText(semesterName);
    if (!normalizedSemester) {
      return { academicYear: '', semester: '' };
    }

    const fullMatch = normalizedSemester.match(/^(\d{4})-(\d{4})\s+SEM\s+(\d+)$/i);
    if (fullMatch) {
      return {
        academicYear: `${fullMatch[1]}-${fullMatch[2].slice(-2)}`,
        semester: `Semester ${fullMatch[3]}`
      };
    }

    const shortMatch = normalizedSemester.match(/^(\d{4})-(\d{2})\s+SEM\s+(\d+)$/i);
    if (shortMatch) {
      return {
        academicYear: `${shortMatch[1]}-${shortMatch[2]}`,
        semester: `Semester ${shortMatch[3]}`
      };
    }

    return { academicYear: '', semester: '' };
  };

  const normalizeCourseRecord = (course) => {
    const courseCode = normalizeText(course.course_code || course.course_id || course.courseCode || '');
    const courseName = normalizeText(course.course_name || course.course_title || course.courseName || '');
    const batchName = normalizeText(course.batch_name || course.batch || '');
    const semesterName = normalizeText(course.semester_name || course.semester || '');
    const examTypeValue = course.exam_type || course.examType || '';
    const facultyEmailId = normalizeText(course.faculty_email_id || course.facultyEmailId || course.instructor_email || '');

    return {
      course_code: courseCode,
      course_name: courseName,
      batch_name: batchName,
      semester_name: semesterName,
      faculty_email_id: facultyEmailId,
      exam_type: getExamTypeLabel(examTypeValue),
      evaluation_type: getEvaluationTypeLabel(examTypeValue, course.evaluation_type || course.evaluationType || ''),
      course_key: getCourseKey(courseCode, courseName)
    };
  };

  const [availableCourses, setAvailableCourses] = useState([]);
  const [courseRecords, setCourseRecords] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [showCourseNameDropdown, setShowCourseNameDropdown] = useState(false);
  const [showCourseIdDropdown, setShowCourseIdDropdown] = useState(false);
  const [courseNameSearch, setCourseNameSearch] = useState('');
  const [courseIdSearch, setCourseIdSearch] = useState('');
  const [selectedCourseKey, setSelectedCourseKey] = useState('');
  const [evaluationDetails, setEvaluationDetails] = useState({
    examType: '',
    examFormat: '',
    academicYear: '',
    semester: '',
    programCommittee: '',
    batch: '',
    evaluationType: '',
    instructorName: '',
    courseId: '',
    courseName: ''
  });
  const [userData, setUserData] = useState({});
  const [academicYearError, setAcademicYearError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [questionPapers, setQuestionPapers] = useState(null);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewContentType, setPreviewContentType] = useState('');
  const [fetchedPaperBlob, setFetchedPaperBlob] = useState(null);
  const [fetchedPaperFilename, setFetchedPaperFilename] = useState('question-paper.pdf');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePapers, setDuplicatePapers] = useState([]);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [isReplacingUpload, setIsReplacingUpload] = useState(false);

  const academicYearPattern = /^\d{4}-\d{2}$/;

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const extractErrorMessage = (error) => {
    if (typeof error === 'string') {
      // Remove any JSON wrapping first
      let cleanError = error;
      if (cleanError.includes('{') && cleanError.includes('}')) {
        try {
          const parsed = JSON.parse(cleanError);
          cleanError = parsed.detail || parsed.message || cleanError;
        } catch {
          // If parsing fails, remove JSON-like content
          cleanError = cleanError.replace(/\{[^}]*\}/g, '').trim();
        }
      }
      return cleanError || error;
    }
    return String(error);
  };


  const getFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) {
      return 'question-paper.pdf';
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim().replace(/^\"|\"$/g, ''));
      } catch {
        return utf8Match[1].trim().replace(/^\"|\"$/g, '');
      }
    }

    const filenameMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].trim();
    }

    return 'question-paper.pdf';
  };

  const waitForPaperQueued = async (paperId, maxAttempts = 30, intervalMs = 1000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const response = await fetch(`${getApiUrl(SERVERS.UPLOAD, ENDPOINTS.QUESTION_PAPER)}/${paperId}`);
      if (response.ok) {
        return true;
      }
      await sleep(intervalMs);
    }
    return false;
  };

  const selectedCourseRecords = useMemo(
    () => courseRecords.filter((record) => record.course_key === selectedCourseKey),
    [courseRecords, selectedCourseKey]
  );

  const availableExamTypes = useMemo(() => {
    const seenExamTypes = new Set();

    return selectedCourseRecords
      .filter((record) => record.exam_type)
      .filter((record) => {
        const key = record.exam_type.toLowerCase();
        if (seenExamTypes.has(key)) {
          return false;
        }
        seenExamTypes.add(key);
        return true;
      })
      .map((record) => ({
        value: record.exam_type,
        label: record.exam_type,
        description: [record.batch_name, record.semester_name, record.evaluation_type || ''].filter(Boolean).join(' • '),
        record
      }));
  }, [selectedCourseRecords]);

  const selectedInstructorName = useMemo(() => {
    const matchedRecord = selectedCourseRecords.find((record) => record.faculty_email_id);
    return matchedRecord?.faculty_email_id || '';
  }, [selectedCourseRecords]);


  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoadingUserData(true);
      try {
        const response = await fetch('/qpra/get-saml-data', {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            window.location.href = '/qprs/login';
            return;
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error('Error fetching SAML user data:', error);
      } finally {
        setIsLoadingUserData(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoadingCourses(true);
      try {
        const url = getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPLOAD_COURSE_DATA);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch courses');
        }

        const data = await response.json();
        const normalizedRecords = (data.courses || [])
          .map(normalizeCourseRecord)
          .filter((course) => course.course_code && course.course_name);

        const uniqueCourses = [];
        const seenCourses = new Set();

        normalizedRecords.forEach((course) => {
          if (seenCourses.has(course.course_key)) {
            return;
          }
          seenCourses.add(course.course_key);
          uniqueCourses.push({
            course_code: course.course_code,
            course_name: course.course_name,
            course_key: course.course_key
          });
        });

        setCourseRecords(normalizedRecords);
        setAvailableCourses(uniqueCourses);
      } catch (error) {
        console.error('Error fetching courses for automated evaluation:', error);
        setCourseRecords([]);
        setAvailableCourses([]);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDetailsChange = (event) => {
    const { name, value } = event.target;

    if (name === 'academicYear') {
      if (value === '' || academicYearPattern.test(value)) {
        setAcademicYearError('');
      } else {
        setAcademicYearError('Academic Year must be in YYYY-YY format, for example 2024-25.');
      }
    }

    setEvaluationDetails((prev) => {
      const next = {
        ...prev,
        [name]: value
      };

      if (name === 'examType') {
        const matchedExamType = selectedCourseRecords.find(
          (record) => record.exam_type.toLowerCase() === value.toLowerCase()
        );

        if (matchedExamType) {
          next.batch = matchedExamType.batch_name;
          next.evaluationType = matchedExamType.evaluation_type;
          next.instructorName = matchedExamType.faculty_email_id || '';

          const semesterDetails = parseSemesterName(matchedExamType.semester_name);
          if (semesterDetails.academicYear) {
            next.academicYear = semesterDetails.academicYear;
          }
          if (semesterDetails.semester) {
            next.semester = semesterDetails.semester;
          }
        }
      }

      if (name === 'courseId') {
        const matchedCourse = availableCourses.find(
          (course) => course.course_code.toLowerCase() === value.toLowerCase()
        );

        if (matchedCourse) {
          next.courseName = matchedCourse.course_name;
          next.instructorName = selectedInstructorName;
        }
      }

      if (name === 'courseName') {
        const matchedCourse = availableCourses.find(
          (course) => course.course_name.toLowerCase() === value.toLowerCase()
        );

        if (matchedCourse) {
          next.courseId = matchedCourse.course_code;
          next.instructorName = selectedInstructorName;
        }
      }

      return next;
    });
  };

  const handleAcademicYearBlur = () => {
    if (!evaluationDetails.academicYear) {
      setAcademicYearError('Academic Year is required.');
      return;
    }

    if (!academicYearPattern.test(evaluationDetails.academicYear)) {
      setAcademicYearError('Academic Year must be in YYYY-YY format, for example 2024-25.');
      return;
    }

    setAcademicYearError('');
  };

  const handleCourseNameSearchChange = (event) => {
    const { value } = event.target;
    setCourseNameSearch(value);
    setShowCourseNameDropdown(true);
    setSelectedCourseKey('');
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: '',
      courseId: '',
      examType: '',
      batch: '',
      evaluationType: '',
      academicYear: '',
      semester: ''
    }));
  };

  const handleCourseIdSearchChange = (event) => {
    const { value } = event.target;
    setCourseIdSearch(value);
    setShowCourseIdDropdown(true);
    setSelectedCourseKey('');
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: '',
      courseId: '',
      examType: '',
      batch: '',
      evaluationType: '',
      academicYear: '',
      semester: ''
    }));
  };

  const normalizedCourseNameQuery = courseNameSearch.trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedCourseIdQuery = courseIdSearch.trim().replace(/\s+/g, ' ').toLowerCase();

  const filteredCoursesByName = useMemo(
    () => availableCourses.filter((course) =>
      course.course_name.toLowerCase().includes(normalizedCourseNameQuery)
    ),
    [availableCourses, normalizedCourseNameQuery]
  );

  const filteredCoursesById = useMemo(
    () => availableCourses.filter((course) =>
      course.course_code.toLowerCase().includes(normalizedCourseIdQuery)
    ),
    [availableCourses, normalizedCourseIdQuery]
  );

  const handleCourseSelect = (course) => {
    const matchedInstructorName = courseRecords.find((record) => record.course_key === course.course_key && record.faculty_email_id)?.faculty_email_id || '';

    setCourseNameSearch(course.course_name);
    setCourseIdSearch(course.course_code);
    setSelectedCourseKey(course.course_key);
    setEvaluationDetails((prev) => ({
      ...prev,
      courseName: course.course_name,
      courseId: course.course_code,
      examType: '',
      batch: '',
      evaluationType: '',
      instructorName: matchedInstructorName,
      academicYear: '',
      semester: ''
    }));
    setShowCourseNameDropdown(false);
    setShowCourseIdDropdown(false);
  };

  const validateForm = () => {
    const errors = [];

    if (!evaluationDetails.courseName.trim()) {
      errors.push('Course Name is required');
    }
    if (!evaluationDetails.courseId.trim()) {
      errors.push('Course ID is required');
    }
    if (!evaluationDetails.examType.trim()) {
      errors.push('Exam Type is required');
    }
    if (!evaluationDetails.batch.trim()) {
      errors.push('Batch is required');
    }
    if (!evaluationDetails.academicYear.trim()) {
      errors.push('Academic Year is required');
    }
    if (!academicYearPattern.test(evaluationDetails.academicYear)) {
      errors.push('Academic Year must be in YYYY-YY format');
    }
    if (!evaluationDetails.semester) {
      errors.push('Semester is required');
    }
    if (!evaluationDetails.examFormat) {
      errors.push('Exam Format is required');
    }
    if (!evaluationDetails.programCommittee) {
      errors.push('Program Committee is required');
    }

    return errors;
  };

  const handleFetchQuestionPapers = async () => {
    setFetchError('');
    setQuestionPapers(null);
    setEvaluationResult(null);
    setFetchedPaperBlob(null);
    setFetchedPaperFilename('question-paper.pdf');
    setPreviewContentType('');
    setPreviewUrl((prev) => {
      if (prev) {
        window.URL.revokeObjectURL(prev);
      }
      return '';
    });

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setFetchError(validationErrors.join(', '));
      return;
    }

    setIsFetching(true);

    try {
      const normalizedExamType = evaluationDetails.examType.trim().toUpperCase();
      const evaluationType = evaluationDetails.evaluationType.trim() || null;
      
      // Convert academic year from YYYY-YY format to YYYY-YYYY format
      // Example: "2024-25" becomes "2024-2025"
      const [year, yearEnd] = evaluationDetails.academicYear.split('-');
      const fullYearEnd = year.substring(0, 2) + yearEnd; // "20" + "25" = "2025"
      const fullAcademicYear = `${year}-${fullYearEnd}`;
      
      const semesterNumber = evaluationDetails.semester.split(' ')[1];
      const semesterFormatted = `${fullAcademicYear} SEM ${semesterNumber}`;

      const response = await fetch('/upload/get-question-papers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          course_code: evaluationDetails.courseId,
          batch: evaluationDetails.batch.trim(),
          exam_type: normalizedExamType,
          evaluation_type: evaluationType,
          semester: semesterFormatted
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.detail || errorData.message || 'No question papers found for the given details';
        errorMessage = extractErrorMessage(errorMessage);

        if (
          response.status === 404 ||
          errorMessage === 'Failed to fetch question papers' ||
          errorMessage.startsWith('Failed to fetch question papers:')
        ) {
          errorMessage = 'Question paper not found';
        }

        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type') || '';
      const contentDisposition = response.headers.get('content-disposition') || '';

      if (contentType.toLowerCase().includes('application/json')) {
        const data = await response.json();
        setQuestionPapers(data);
      } else {
        const blob = await response.blob();
        const fileUrl = window.URL.createObjectURL(blob);
        const extractedFilename = getFilenameFromContentDisposition(contentDisposition);

        setFetchedPaperBlob(blob);
        setFetchedPaperFilename(extractedFilename || 'question-paper.pdf');
        setPreviewUrl(fileUrl);
        setPreviewContentType(contentType.toLowerCase());

        setQuestionPapers({
          status: 'success',
          message: 'Question paper loaded successfully.'
        });

        if (!contentType.toLowerCase().includes('pdf')) {
          setFetchError('Received a non-PDF file from upstream. Please check exam setup or upstream format.');
        }
      }
    } catch (error) {
      let cleanError = extractErrorMessage(error.message || 'An error occurred while fetching question papers');

      if (
        cleanError === 'Failed to fetch question papers' ||
        cleanError.startsWith('Failed to fetch question papers:')
      ) {
        cleanError = 'Question paper not found';
      }

      setFetchError(cleanError);
      console.error('Error fetching question papers:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEvaluate = async () => {
    setFetchError('');
    setEvaluationResult(null);

    if (!fetchedPaperBlob) {
      setFetchError('Fetch a question paper first before running evaluation.');
      return;
    }

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setFetchError(validationErrors.join(', '));
      return;
    }

    if (!userData?.email || !userData?.name) {
      setFetchError('Unable to identify logged-in user. Please re-login and try again.');
      return;
    }

    setIsCheckingDuplicate(true);

    try {
      const duplicateParams = new URLSearchParams({
        course_id: evaluationDetails.courseId.trim(),
        exam_type: evaluationDetails.examType.trim(),
        semester: evaluationDetails.semester.split(' ')[1],
        academic_year: evaluationDetails.academicYear.trim()
      });

      const duplicateResponse = await fetch(`/upload/check-duplicate?${duplicateParams}`);
      if (duplicateResponse.ok) {
        const duplicateResult = await duplicateResponse.json();
        if (duplicateResult.has_duplicates) {
          setDuplicatePapers(duplicateResult.duplicates || []);
          setShowDuplicateModal(true);
          return;
        }
      }
    } catch (duplicateError) {
      console.error('Error checking duplicate papers in automated evaluation:', duplicateError);
    } finally {
      setIsCheckingDuplicate(false);
    }

    await performUpload(false);
  };

  const performUpload = async (replaceExisting = false) => {
    setIsEvaluating(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('course_id', evaluationDetails.courseId.trim());
      uploadFormData.append('course_name', evaluationDetails.courseName.trim());
      uploadFormData.append('instructor_name', evaluationDetails.instructorName.trim() || '');
      uploadFormData.append('academic_year', evaluationDetails.academicYear.trim());
      uploadFormData.append('semester', evaluationDetails.semester.split(' ')[1]);
      uploadFormData.append('exam_type', evaluationDetails.examType.trim());
      uploadFormData.append('open_closed_book', evaluationDetails.examFormat.trim());
      uploadFormData.append('uploader_name', userData.name || '');
      uploadFormData.append('uploader_email', userData.email || '');
      uploadFormData.append('program_committee', evaluationDetails.programCommittee.trim());
      uploadFormData.append('custom_fields', JSON.stringify({}));
      uploadFormData.append('upload_type', 'paper');
      uploadFormData.append('replace_existing', replaceExisting ? 'true' : 'false');

      const extension = previewContentType.includes('pdf') ? 'pdf' : 'docx';
      
      // Generate filename in format: CourseCode ExamType Semester AcademicYear.extension
      // Example: AE ZG623 EC-2M SEM1 2025-2026.pdf
      const semesterMatch = evaluationDetails.semester.match(/(\d+)/);
      const semesterNumber = semesterMatch ? semesterMatch[1] : '';
      const semesterLabel = semesterNumber ? `SEM${semesterNumber}` : evaluationDetails.semester;
      
      const generatedFilename = `${evaluationDetails.courseId} ${evaluationDetails.examType} ${semesterLabel} ${evaluationDetails.academicYear}.${extension}`;
      const uploadFile = new File([fetchedPaperBlob], generatedFilename, {
        type: fetchedPaperBlob.type || (previewContentType || 'application/pdf')
      });
      uploadFormData.append('files', uploadFile);

      const uploadResponse = await fetch(getApiUrl(SERVERS.UPLOAD, ENDPOINTS.UPLOAD_PAPERS), {
        method: 'POST',
        body: uploadFormData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        let errorMessage = errorData.detail || errorData.message || 'Upload failed';
        errorMessage = extractErrorMessage(errorMessage);
        throw new Error(errorMessage);
      }

      const uploadResult = await uploadResponse.json();
      const paperId = uploadResult?.paper_ids?.[0];

      if (!paperId) {
        throw new Error('Upload succeeded but no paper ID was returned for evaluation.');
      }

      await waitForPaperQueued(paperId);

      setEvaluationResult({
        paperId,
        uploadResult
      });
      setQuestionPapers({
        status: 'success',
        message: 'Question paper uploaded successfully. Partitioning is running in background. Analysis will run when triggered from Review/Analyze flow.'
      });
    } catch (error) {
      const cleanError = extractErrorMessage(error.message || 'Failed to run automated evaluation.');
      setFetchError(cleanError);
      console.error('Error in automated evaluation pipeline:', error);
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleConfirmReplaceUpload = async () => {
    setShowDuplicateModal(false);
    setIsReplacingUpload(true);
    setFetchError('');

    try {
      await performUpload(true);
    } finally {
      setIsReplacingUpload(false);
    }
  };

  const handleCancelReplaceUpload = () => {
    setShowDuplicateModal(false);
    setDuplicatePapers([]);
  };

  return (
    <div className="automated-evaluation-page">
      <div className="container">
        <div className="automated-evaluation-card">
          <div className="card-header">
            <div className="card-title-section">
              <div className="card-icon-header">
                <div className="card-icon">
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <div className="card-title-text">
                  <h1>Automated Evaluation</h1>
                  <p>This page is ready for automated evaluation workflow.</p>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="back-to-upload-btn"
              onClick={() => navigate('/upload_page')}
            >
              ← Back to Upload Question Papers
            </button>
          </div>
        </div>

        {showDuplicateModal && duplicatePapers.length > 0 && (
          <div className="ae-modal-overlay" onClick={handleCancelReplaceUpload}>
            <div className="ae-modal-content" onClick={(event) => event.stopPropagation()}>
              <div className="ae-modal-header">
                <h2>Paper Already Uploaded</h2>
                <button type="button" className="ae-modal-close" onClick={handleCancelReplaceUpload}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="ae-modal-body">
                <div className="ae-duplicate-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <p>Paper for this course has been uploaded. Do you want to replace?</p>
                </div>

                <div className="ae-duplicate-list">
                  {duplicatePapers.map((paper) => (
                    <div key={paper.id} className="ae-duplicate-item">
                      <p><strong>File:</strong> {paper.original_filename}</p>
                      <p><strong>Course:</strong> {paper.course_id} - {paper.course_name}</p>
                      <p><strong>Exam Type:</strong> {paper.exam_type}</p>
                      <p><strong>Uploaded on:</strong> {new Date(paper.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ae-modal-footer">
                <button
                  type="button"
                  className="ae-btn-cancel"
                  onClick={handleCancelReplaceUpload}
                  disabled={isReplacingUpload}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ae-btn-replace"
                  onClick={handleConfirmReplaceUpload}
                  disabled={isReplacingUpload}
                >
                  {isReplacingUpload ? 'Replacing...' : 'Replace'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="automated-evaluation-card evaluation-details-card">
          <div className="evaluation-details-header">
            <h2>Evaluation Details</h2>
            <p>Configure the academic and course details before running automated evaluation.</p>
          </div>

          {isLoadingCourses && (
            <div className="loading-state-message">
              <div className="loading-spinner"></div>
              <p>Loading course data...</p>
            </div>
          )}

          {isLoadingUserData && (
            <div className="loading-state-message">
              <div className="loading-spinner"></div>
              <p>Loading user data...</p>
            </div>
          )}

          <div className="evaluation-details-grid">
            <div className="evaluation-form-group">
              <label htmlFor="courseName" className="required-field">Course Name</label>
              <div className="search-dropdown-container">
                <input
                  id="courseName"
                  name="courseName"
                  type="text"
                  value={courseNameSearch}
                  onChange={handleCourseNameSearchChange}
                  onInput={handleCourseNameSearchChange}
                  onFocus={() => setShowCourseNameDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCourseNameDropdown(false), 150)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="evaluation-input"
                  placeholder="Search course name"
                  required
                />
                {showCourseNameDropdown && (
                  <div className="search-dropdown-list" key={`name-${normalizedCourseNameQuery}`}>
                    {filteredCoursesByName.length > 0 ? (
                      filteredCoursesByName.map((course) => (
                        <div
                          key={`name-${course.course_code}-${course.course_name}`}
                          className="search-dropdown-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleCourseSelect(course);
                          }}
                        >
                          <div className="dropdown-primary">{course.course_name}</div>
                          <div className="dropdown-secondary">{course.course_code}</div>
                        </div>
                      ))
                    ) : (
                      <div className="search-dropdown-empty">No matching courses</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="courseId" className="required-field">Course ID</label>
              <div className="search-dropdown-container">
                <input
                  id="courseId"
                  name="courseId"
                  type="text"
                  value={courseIdSearch}
                  onChange={handleCourseIdSearchChange}
                  onInput={handleCourseIdSearchChange}
                  onFocus={() => setShowCourseIdDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCourseIdDropdown(false), 150)}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="evaluation-input"
                  placeholder="Search course ID"
                  required
                />
                {showCourseIdDropdown && (
                  <div className="search-dropdown-list" key={`id-${normalizedCourseIdQuery}`}>
                    {filteredCoursesById.length > 0 ? (
                      filteredCoursesById.map((course) => (
                        <div
                          key={`id-${course.course_code}-${course.course_name}`}
                          className="search-dropdown-item"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleCourseSelect(course);
                          }}
                        >
                          <div className="dropdown-primary">{course.course_code}</div>
                          <div className="dropdown-secondary">{course.course_name}</div>
                        </div>
                      ))
                    ) : (
                      <div className="search-dropdown-empty">No matching courses</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="academicYear" className="required-field">Academic Year</label>
              <input
                id="academicYear"
                name="academicYear"
                type="text"
                value={evaluationDetails.academicYear}
                onChange={handleDetailsChange}
                onBlur={handleAcademicYearBlur}
                className={`evaluation-input ${academicYearError ? 'input-error' : ''}`}
                placeholder="YYYY-YY"
                pattern="\d{4}-\d{2}"
                title="Use the format YYYY-YY, for example 2024-25"
                maxLength={7}
                required
              />
              <span className={`field-hint ${academicYearError ? 'field-error' : ''}`}>
                {academicYearError || 'Use the exact format like 2024-25.'}
              </span>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="semester" className="required-field">Semester</label>
              <select
                id="semester"
                name="semester"
                value={evaluationDetails.semester}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Semester</option>
                <option value="Semester 1">Semester 1</option>
                <option value="Semester 2">Semester 2</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="examType" className="required-field">Exam Type</label>
              <select
                id="examType"
                name="examType"
                value={evaluationDetails.examType}
                onChange={handleDetailsChange}
                className="evaluation-input"
                disabled={!selectedCourseKey || availableExamTypes.length === 0}
                required
              >
                <option value="">
                  {selectedCourseKey ? 'Select Exam Type' : 'Select a course first'}
                </option>
                {availableExamTypes.map((option) => (
                  <option key={option.value} value={option.value} title={option.description}>
                    {option.value}
                  </option>
                ))}
              </select>
          
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="examFormat" className="required-field">Exam Format</label>
              <select
                id="examFormat"
                name="examFormat"
                value={evaluationDetails.examFormat}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Exam Format</option>
                <option value="open">Open Book</option>
                <option value="closed">Closed Book</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="programCommittee" className="required-field">Program Committee</label>
              <select
                id="programCommittee"
                name="programCommittee"
                value={evaluationDetails.programCommittee}
                onChange={handleDetailsChange}
                className="evaluation-input"
                required
              >
                <option value="">Select Program Committee</option>
                <option value="CSIS">CSIS</option>
                <option value="Core Engg.">Core Engg.</option>
                <option value="Management">Management</option>
                <option value="EEE">EEE</option>
              </select>
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="batch" className="required-field">Batch</label>
              <input
                id="batch"
                name="batch"
                type="text"
                value={evaluationDetails.batch}
                className="evaluation-input"
                placeholder="Auto-filled after exam type selection"
                readOnly
                required
              />
            </div>

            <div className="evaluation-form-group">
              <label htmlFor="instructorName">Instructor Name</label>
              <input
                id="instructorName"
                name="instructorName"
                type="text"
                value={evaluationDetails.instructorName || ''}
                className="evaluation-input"
                placeholder="Faculty email ID from selected course"
                readOnly
              />
       
            </div>
{/* 
            <div className="evaluation-form-group">
              <label htmlFor="evaluationType">Evaluation Type</label>
              <input
                id="evaluationType"
                name="evaluationType"
                type="text"
                value={evaluationDetails.evaluationType}
                className="evaluation-input"
                placeholder="Optional; auto-filled when available"
                readOnly
              />
            </div> */}

          </div>
        </div>

        {fetchError && (
          <div className="error-alert">
            {fetchError}
          </div>
        )}

        {questionPapers && (
          <div className="success-alert">
            <strong>Success!</strong> Question papers fetched successfully.
          </div>
        )}

        {evaluationResult && (
          <div className="success-alert">
            <strong>Uploaded Successfully!</strong> Question paper uploaded and processing started.
          </div>
        )}

        {previewUrl && previewContentType.includes('pdf') && (
          <div className="automated-evaluation-card pdf-preview-card">
            <div className="evaluation-details-header">
              <h2>Question Paper PDF Preview</h2>
              <p>Preview generated from latest fetch result.</p>
            </div>
            <iframe
              title="Question Paper PDF Preview"
              src={previewUrl}
              className="pdf-preview-frame"
            />
          </div>
        )}

        <div className="evaluation-actions">
          <button
            type="button"
            className="btn-fetch-papers"
            onClick={handleFetchQuestionPapers}
            disabled={isFetching || isEvaluating || isCheckingDuplicate || isReplacingUpload}
          >
            {isFetching ? 'Fetching...' : 'Fetch Question Papers'}
          </button>
          <button
            type="button"
            className="btn-fetch-papers"
            onClick={handleEvaluate}
            disabled={isEvaluating || isFetching || isCheckingDuplicate || isReplacingUpload || !fetchedPaperBlob}
          >
            {isCheckingDuplicate ? 'Checking...' : isEvaluating ? 'Uploading...' : 'Upload Question Paper'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomatedEvaluation;
